"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { SHIMMER_BLUR_DATA_URL } from "@/lib/constants"
import { useDomain } from "@/lib/domain-context"
import { buildWatermarkedUrl } from "@/lib/storage/public-url"
import { useTranslations } from "@/lib/i18n/client"
import { AeronaveNoPhoto } from "@/components/public/AeronaveNoPhoto"
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { Dialog as DialogPrimitive } from "radix-ui"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"

interface Photo {
  id: string
  storage_path: string
  descricao: string | null
}

interface AeronaveGalleryProps {
  photos: Photo[]
  alt: string
}

const SWIPE_THRESHOLD_PX = 50

export function AeronaveGallery({ photos, alt }: AeronaveGalleryProps) {
  const { t } = useTranslations()
  const { domainKey } = useDomain()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [imageOpacity, setImageOpacity] = useState(1)
  const touchStartXRef = useRef<number | null>(null)

  // Defesa: se o array de fotos encolher (mutação inesperada), volta ao índice 0.
  useEffect(() => {
    if (selectedIndex >= photos.length) {
      setSelectedIndex(0)
    }
  }, [photos.length, selectedIndex])

  const changePhoto = useCallback(
    (newIndex: number) => {
      if (newIndex === selectedIndex) return
      setImageOpacity(0)
      setTimeout(() => {
        setSelectedIndex(newIndex)
        setImageOpacity(1)
      }, 200)
    },
    [selectedIndex],
  )

  const goNext = useCallback(() => {
    if (photos.length > 1) {
      changePhoto((selectedIndex + 1) % photos.length)
    }
  }, [photos.length, selectedIndex, changePhoto])

  const goPrev = useCallback(() => {
    if (photos.length > 1) {
      changePhoto((selectedIndex - 1 + photos.length) % photos.length)
    }
  }, [photos.length, selectedIndex, changePhoto])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const startX = touchStartXRef.current
      const endX = e.changedTouches[0]?.clientX
      touchStartXRef.current = null
      if (startX == null || endX == null) return
      const delta = endX - startX
      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return
      if (delta > 0) goPrev()
      else goNext()
    },
    [goNext, goPrev],
  )

  useEffect(() => {
    if (!lightboxOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowRight") goNext()
      if (e.key === "ArrowLeft") goPrev()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [lightboxOpen, goNext, goPrev])

  if (photos.length === 0) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-xl">
        <AeronaveNoPhoto domainKey={domainKey} />
      </div>
    )
  }

  // Fallback final caso o índice esteja fora dos limites entre o render e o effect.
  const currentPhoto = photos[selectedIndex] ?? photos[0]
  const currentPhotoUrl = buildWatermarkedUrl(currentPhoto.storage_path, domainKey) ?? ""

  return (
    <div className="w-full min-w-0">
      {/* Main Image */}
      <div
        className="group/gallery relative aspect-video w-full overflow-hidden rounded-xl bg-slate-100"
        onTouchStart={photos.length > 1 ? handleTouchStart : undefined}
        onTouchEnd={photos.length > 1 ? handleTouchEnd : undefined}
      >
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          aria-label={t.detail.galleryOpen}
          className="relative block h-full w-full cursor-pointer"
        >
          <Image
            src={currentPhotoUrl}
            alt={currentPhoto.descricao || alt}
            fill
            className="object-cover transition-opacity duration-200"
            style={{ opacity: imageOpacity }}
            priority
            sizes="(max-width: 1024px) 100vw, 60vw"
            placeholder="blur"
            blurDataURL={SHIMMER_BLUR_DATA_URL}
          />
        </button>

        {/* Prev/Next arrows on main image — sempre visíveis em mobile (sem hover); fade-in em desktop. */}
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label={t.detail.galleryPrev}
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-3.5 text-navy-800 transition-opacity duration-200 hover:bg-white md:left-3 md:p-2.5 md:opacity-0 md:group-hover/gallery:opacity-100"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label={t.detail.galleryNext}
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-3.5 text-navy-800 transition-opacity duration-200 hover:bg-white md:right-3 md:p-2.5 md:opacity-0 md:group-hover/gallery:opacity-100"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </>
        )}

        {/* Photo counter */}
        {photos.length > 1 && (
          <span className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2.5 py-0.5 text-xs text-white">
            {selectedIndex + 1} / {photos.length}
          </span>
        )}
      </div>

      {/* Thumbnails — maiores em mobile (touch-friendly) com snap-x para scroll natural. */}
      {photos.length > 1 && (
        <ScrollArea className="mt-3">
          <div className="flex snap-x snap-mandatory gap-2 pb-2">
            {photos.map((photo, i) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => changePhoto(i)}
                aria-label={t.detail.galleryThumbnail.replace("{index}", String(i + 1)).replace("{total}", String(photos.length))}
                aria-current={i === selectedIndex ? "true" : undefined}
                className={cn(
                  "relative h-16 w-24 shrink-0 snap-start overflow-hidden rounded-lg transition-opacity md:h-15 md:w-20",
                  i === selectedIndex
                    ? "border-2 border-navy-500 opacity-100"
                    : "border-[0.5px] border-slate-200 opacity-70 hover:opacity-100",
                )}
              >
                <Image
                  src={buildWatermarkedUrl(photo.storage_path, domainKey) ?? ""}
                  alt={photo.descricao || `${alt} - foto ${i + 1}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 96px, 80px"
                />
              </button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-black/90" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-50 flex items-center justify-center outline-none"
            onTouchStart={photos.length > 1 ? handleTouchStart : undefined}
            onTouchEnd={photos.length > 1 ? handleTouchEnd : undefined}
          >
            <DialogTitle className="sr-only">
              Galeria de fotos — {alt}
            </DialogTitle>

            {/* Close button */}
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label={t.detail.galleryClose}
              className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-3.5 text-white transition-colors hover:bg-white/20 md:p-3"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Prev button */}
            {photos.length > 1 && (
              <button
                type="button"
                onClick={goPrev}
                aria-label={t.detail.galleryPrev}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
            )}

            {/* Image */}
            <div className="relative inline-block">
              <Image
                src={currentPhotoUrl}
                alt={currentPhoto.descricao || alt}
                width={1200}
                height={800}
                className="max-h-[90vh] max-w-[90vw] object-contain"
                sizes="90vw"
              />
            </div>

            {/* Next button */}
            {photos.length > 1 && (
              <button
                type="button"
                onClick={goNext}
                aria-label={t.detail.galleryNext}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            )}

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
              {selectedIndex + 1} / {photos.length}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  )
}
